import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ClubAttributesFormComponent } from '~features/club-overview/components/club-attributes-form/club-attributes-form.component';
import { TablesTranslations } from '~shared/components/tables/tables-translations.service';
import { ModalService } from '~shared/components/elements/global-modal/modal.service';
import { ActionButtonType, NotificationType } from '~core/enum';
import { SidebarService } from '~core/components/sidebar/sidebar.service';
import { ActionButton, ActionButtonEvent, Attribute, Club, ExportRequest, SidebarOptions } from '~core/models';
import { AttributeService, ClubService, DefaultNotificationService, ExportService, NavigationService, StorageService } from '~core/services';
import { BasicStatusTranslationService, TooltipTranslationService } from '~shared/translations';

/**
 * Opearte the attributes on a certain club-overview
 */
@UntilDestroy()
@Component({
  selector: 'app-club-attributes',
  templateUrl: './club-attributes.component.html',
})
export class ClubAttributesComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  public selectedClub: Club;
  public updateDataLoading: boolean = false;
  public sidebarOpened: boolean = false;
  public exportRequest?: ExportRequest = null;

  public actionButtons: Array<ActionButton> = [];
  public columns: Array<string> = [];
  public data: Array<Attribute> = [];
  public keys: Array<Array<string>> = [];
  public baseApi: string;
  public customQueries: Array<any> = [];
  public i18nDeleteTitle: string;
  public i18nDeleteParagraph: string;
  public i18nEmptyState: string;

  @ViewChild(ClubAttributesFormComponent, { static: true }) public clubAttributesFormComponent: ClubAttributesFormComponent;

  constructor(
    public clubService: ClubService,
    public attributeService: AttributeService,
    public basicStatusTranslationService: BasicStatusTranslationService,
    public route: ActivatedRoute,
    public storage: StorageService,
    public sidebarService: SidebarService,
    public navigationService: NavigationService,
    public tooltipTranslations: TooltipTranslationService,
    public tablesTranslations: TablesTranslations,
    public modalService: ModalService,
    public exportService: ExportService,
    private translations: BasicStatusTranslationService,
    public notificationService: DefaultNotificationService,
  ) {
    this.sidebarService.setSidebar(new SidebarOptions(false, false));
    this.navigationService.setCurrentPage('clubAttributes');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'clubAttributes']);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe((club: Club) => this.selectedClub = club);
    this.attributeService.castClubAttributes.pipe(untilDestroyed(this))
      .subscribe((att: any) => this.data = att);
    this.actionButtons.push(
      new ActionButton({
        type: 'noclick',
        icon: '#esf-icon-circle-check',
        tooltip: this.tooltipTranslations.i18nCustomerPoolSynced,
        hideKey: 'customerPoolGroupId'
      }),
      new ActionButton({
        type: ActionButtonType.MODAL,
        icon: '#esf-icon-download',
        tooltip: this.tooltipTranslations.i18nDownloadCustomerList,
        disabled: 'exportInProgress'
      }),
      new ActionButton({
        type: ActionButtonType.EDIT,
        icon: '#esf-icon-edit',
        tooltip: this.tooltipTranslations.i18nEdit,
      }),
      new ActionButton({
        type: ActionButtonType.DELETE,
        icon: '#esf-icon-trash',
        tooltip: this.tooltipTranslations.i18nDelete,
        disabled: { key: 'internal', value: true }
      })
    );
    this.keys.push(['coloredName', 'description', 'assignedCustomersCount', 'statusObject']);
    this.columns.push(
      this.tablesTranslations.i18nName,
      this.tablesTranslations.i18nDescription,
      this.tablesTranslations.i18nAssignedCustomers,
      this.tablesTranslations.i18nStatus,
    );
    this.i18nDeleteTitle = $localize`:@@general.modalText.deleteAttributeTitle:Delete Attribute?`;
    this.i18nDeleteParagraph = $localize`:@@general.modalText.deleteAttributeText:Are you certain you want to delete this attribute. This attribute will be deleted from all customers.`;
    this.i18nEmptyState = $localize`:@@general.emptyState.text:There are no attributes`;
  }

  public ngOnDestroy(): void { }

  /**
   * Initialization method
   */
  public ngOnInit(): any {
    this.getClubAttributes(this.selectedClub.id);
  }

  /**
   * Fetch the club-overview data via the url parameter and fill the club-overview form.
   */
  public setClubAttributes(attributes): void {
    const arr = [];
    attributes.forEach(attribute => {
      arr.push(new Attribute(attribute, this.translations));
    });
    this.attributeService.announceClubAttributes(arr);
  }

  /**
   * Get the club-overview users endpoing
   */
  public getClubAttributes(id: number): any {
    const endpoint = this.attributeService.getClubAttributesEndpoint(id);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
    this.loading = false;
  }

  /**
   * Triggers modal for deletion
   */
  public deleteAttribute(id: number): void {
    this.attributeService.deleteClubAttribute(id).pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateDataLoading = false;
        this.modalService.setStatus(false);
        this.attributeService.deleteAttributeFromList(id);
      });
  }

  /**
   * Toggles sidebar modal opened or closed
   *
   * @param status true or false
   */
  public toggleSidebar(status: boolean): void {
    this.sidebarOpened = status;
  }

  /**
   * Opens the modal and removes lingering validation
   */
  public openCreateAttribute(): void {
    this.attributeService.toggleAllModals(false);
    setTimeout(() => {
      this.attributeService.toggleCreateAttributeModal(true);
      this.attributeService.announceCreateAttribute(true);
    }, 100);
  }

  /**
   * Opens the modal and removes lingering validation
   */
  public openEditAttribute(attribute: any): void {
    this.attributeService.toggleAllModals(false);
    setTimeout(() => {
      this.attributeService.toggleEditAttributeModal(true);
      this.attributeService.announceEditAttribute(attribute);
    }, 100);
  }

  /**
   * Sets the submit laoding property
   */
  public setSubmitLoading(status: boolean): void {
    this.updateDataLoading = status;
  }

  public async showDeleteModal(id: number): Promise<void> {
    this.modalService.setModalOptions(this.i18nDeleteTitle, this.i18nDeleteParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.deleteAttribute(id);
    }
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<any>): void {
    switch (actionButton.type) {
      case ActionButtonType.DELETE:
        this.showDeleteModal(actionButton.data.id);
        break;
      case ActionButtonType.EDIT:
        this.openEditAttribute(actionButton.data);
        break;
      case ActionButtonType.MODAL:
        this.makeExportRequest(actionButton.data);
        break;
      default:
    }
  }

  /**
   * Make export request
   */
  makeExportRequest(attribute: Attribute): void {
    const request = {
      club: `/api/clubs/${this.selectedClub.id}`,
      type: 'club-attribute',
      resourceId: attribute.id.toString() // Must be sent as a string
    };
    this.exportService.createExportRequest(request).subscribe((res: any) => {
      attribute.exportInProgress = true;
      this.notificationService.showNotification({
        type: NotificationType.INFO,
        message: this.exportService.i18nExportInitiated,
      });
      const interval = setInterval(() => {
        this.exportService.getExportRequestById(res.id)
          .subscribe({
            next: (res2: any) => {
              if (res2.media.contentUrl !== null) {
                clearInterval(interval);
                this.exportRequest = res2;
                attribute.exportInProgress = false;
                this.notificationService.showNotification({
                  type: NotificationType.SUCCESS,
                  message: `${this.exportService.i18nExportReady} <a class="esf-button esf-button--simple"
              href="${this.exportRequest.media.contentUrl}" target="_blank">${this.exportService.i18nClickHereToDownload}</a>`,
                  duration: undefined,
                });
              }
            },
            error: (error: Error) => {
              clearInterval(interval);
              attribute.exportInProgress = false;
            }
          });
      }, 3000);
    });
  }
}
